/**
 * Admin Roadmap Converter
 * Converts AdminRoadmapWithDays format to RoadmapData format for viewer compatibility
 * 
 * Maps:
 * - day.title → day.topic (what to learn)
 * - day.subtitle → week.goal (week theme)
 * - day.description → day.task (what to do)
 */

import type { AdminRoadmapWithDays, AdminRoadmapDay } from './adminRoadmapService';
import type { RoadmapData } from '@/types';

export function convertAdminRoadmapToRoadmapData(
  adminRoadmap: AdminRoadmapWithDays
): RoadmapData {
  // Get days array, sorted by day_number
  const days = (adminRoadmap.days || []).sort(
    (a, b) => a.day_number - b.day_number
  );

  // Calculate weeks and months
  const daysPerWeek = 7;
  const weeksPerMonth = 4;

  // Group days into weeks
  const weeks: Array<{
    name: string;
    goal: string;
    days: Array<{
      day: number;
      topic: string;
      task: string;
    }>;
  }> = [];

  for (let i = 0; i < days.length; i += daysPerWeek) {
    const weekDays = days.slice(i, i + daysPerWeek);
    const weekNumber = Math.floor(i / daysPerWeek) + 1;

    // Use the subtitle from first day of week as the week goal
    const weekGoal = weekDays[0]?.subtitle || weekDays[0]?.title || `Week ${weekNumber} Goals`;

    weeks.push({
      name: `Week ${weekNumber}`,
      goal: weekGoal,
      days: weekDays.map((day) => ({
        day: day.day_number,
        // title field from admin form becomes the topic
        topic: day.title || `Day ${day.day_number}`,
        // description field from admin form becomes the task
        task: day.description || day.title || `Complete Day ${day.day_number}`,
      })),
    });
  }

  // Group weeks into months
  const months: Array<{
    name: string;
    weeks: Array<{
      name: string;
      goal: string;
      days: Array<{
        day: number;
        topic: string;
        task: string;
      }>;
    }>;
  }> = [];

  for (let i = 0; i < weeks.length; i += weeksPerMonth) {
    const monthWeeks = weeks.slice(i, i + weeksPerMonth);
    const monthNumber = Math.floor(i / weeksPerMonth) + 1;

    months.push({
      name: `Month ${monthNumber}`,
      weeks: monthWeeks,
    });
  }

  // If no months (all days < daysPerWeek * weeksPerMonth), create at least one month
  if (months.length === 0 && weeks.length > 0) {
    months.push({
      name: 'Month 1',
      weeks: weeks,
    });
  }

  // Build the complete RoadmapData object
  const roadmapData: RoadmapData = {
    id: adminRoadmap.id,
    title: adminRoadmap.title,
    goal: adminRoadmap.description || adminRoadmap.title,
    months: months,
    completedDays: [],
    language: 'en',
    intensity: adminRoadmap.difficulty || 'Beginner',
    meta: {
      goal: adminRoadmap.description || adminRoadmap.title,
      duration: `${adminRoadmap.total_days} Days`,
      studyTime: '1-2 hours per day',
    },
    created_at: adminRoadmap.created_at,
  };

  return roadmapData;
}

/**
 * Get a specific day's details from admin roadmap
 */
export function getAdminRoadmapDay(
  adminRoadmap: AdminRoadmapWithDays,
  dayNumber: number
): AdminRoadmapDay | undefined {
  return (adminRoadmap.days || []).find((day) => day.day_number === dayNumber);
}

/**
 * Get video URL for a specific day
 */
export function getAdminRoadmapDayVideo(
  adminRoadmap: AdminRoadmapWithDays,
  dayNumber: number
): string | undefined {
  const day = getAdminRoadmapDay(adminRoadmap, dayNumber);
  return day?.video_url || (day?.video_id ? `https://www.youtube.com/embed/${day.video_id}` : undefined);
}

/**
 * Get full day content for display
 */
export function getAdminRoadmapDayContent(
  adminRoadmap: AdminRoadmapWithDays,
  dayNumber: number
): AdminRoadmapDay | null {
  const day = getAdminRoadmapDay(adminRoadmap, dayNumber);
  return day || null;
}
